import { styled, createStyles } from "@mui/material/styles";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";

type ButtonSize = "xs" | "sm" | "md" | "lg";

type ButtonVariant = "primary" | "secondary" | "white" | "grey" | "text";

type ButtonSizes = {
  [key: string]: {
    width: string;
    height: string;
    padding: string;
  };
};

const defaultButton = createStyles`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
  outline: none;
  border-radius: 8px;
  font-size: 16px;
  line-height: 150%;
  font-weight: 500;
  box-sizing: border-box;
  box-shadow: none;
  text-transform: none;
  
  &:disabled {
    cursor: not-allowed;
  }

  &:hover {
    box-shadow: none;
  }
`;

const PrimaryButton = createStyles(
  ({ theme }: any) => `
  ${defaultButton}

  background-color: ${theme.palette.primary.dark};
  color: ${theme.palette.common.white};

  &:hover {
    background-color: ${theme.palette.primary.main};
  }

  &:disabled {
    background-color: ${theme.palette.customGrey.main};
    color: ${theme.palette.customGrey.dark};

    & svg[fill] {
      fill: ${theme.palette.customGrey.dark};
    }
  
    & svg[stroke] {
      stroke: ${theme.palette.customGrey.dark};
    }
  }

  & svg[fill] {
    fill: ${theme.palette.common.white};
  }

  & svg[stroke] {
    stroke: ${theme.palette.common.white};
  }
`
);

const SecondaryButton = createStyles(
  ({ theme }: any) => `
    ${defaultButton}

    background-color: ${theme.palette.common.white};
    color: ${theme.palette.primary.dark};
    border-color: ${theme.palette.primary.dark};
  
    &:hover {
      background-color: ${theme.palette.primary.main};
      color: ${theme.palette.common.white};
      border-color: ${theme.palette.primary.main};

      & svg[fill] {
        fill: ${theme.palette.common.white};
      }
    
      & svg[stroke] {
        stroke: ${theme.palette.common.white};
      }
    }

    &:disabled {
      background-color: ${theme.palette.common.white};
      color: ${theme.palette.customGrey.main};
      border-color: ${theme.palette.customGrey.main};
      
      & svg[fill] {
        fill: ${theme.palette.customGrey.main};
      }
    
      & svg[stroke] {
        stroke: ${theme.palette.customGrey.main};
      }
    }

    & svg[fill] {
      fill: ${theme.palette.primary.dark};
    }
  
    & svg[stroke] {
      stroke: ${theme.palette.primary.dark};
    }
  `
);

const WhiteButton = createStyles(
  ({ theme }: any) => `
    ${defaultButton}

    background-color: ${theme.palette.common.white};
    color: ${theme.palette.customGrey.dark};
  
    &:hover {
      background-color: ${theme.palette.primary.main};
      color: ${theme.palette.common.white};

      & svg[fill] {
        fill:  ${theme.palette.common.white};
      }
      
      & svg[stroke] {
        stroke: ${theme.palette.common.white};
      } 
    }

    &:disabled {
      background-color: ${theme.palette.common.white};
      color: ${theme.palette.customGrey.main};

      & svg[fill] {
        fill:  ${theme.palette.customGrey.main};
      }
      
      & svg[stroke] {
        stroke:  ${theme.palette.customGrey.main};
      } 
    }
    
    & svg[fill] {
        fill:  ${theme.palette.customGrey.dark};
    }
    
    & svg[stroke] {
        stroke:  ${theme.palette.customGrey.dark};
    }
  `
);

const GreyButton = createStyles(
  ({ theme }: any) => `
    ${defaultButton}

    background-color: ${theme.palette.customGrey.soft};
    color: ${theme.palette.common.black};
  
    &:hover {
      background-color: ${theme.palette.primary.main};
      color: ${theme.palette.common.white};

      & svg[fill] {
        fill:  ${theme.palette.common.white};
      }
    
      & svg[stroke] {
        stroke:  ${theme.palette.common.white};
      }
    }

    &:disabled {
      background-color: ${theme.palette.customGrey.soft};
      color: ${theme.palette.customGrey.dark};

      & svg[fill] {
        fill:  ${theme.palette.customGrey.dark};
      }
    
      & svg[stroke] {
        stroke:  ${theme.palette.customGrey.dark};
      }
    }

    & svg[fill] {
      fill:  ${theme.palette.common.black};
    }
  
    & svg[stroke] {
      stroke:  ${theme.palette.common.black};
    }
  `
);

const TextButton = createStyles(
  ({ theme }: any) => `
      ${defaultButton}
  
      background-color: transparent;
      color: ${theme.palette.customGrey.dark};
      border: none;

      & .MuiTouchRipple-root {
        display: none;
      }
    
      &:hover {
        background-color: transparent;
        color: ${theme.palette.primary.main};

        & svg[fill] {
          fill:  ${theme.palette.primary.main};
        }
      
        & svg[stroke] {
          stroke: ${theme.palette.primary.main};
        }
      }
  
      &:disabled {
        background-color: transparent;
        color: ${theme.palette.customGrey.main};

        & svg[fill] {
          fill:  ${theme.palette.customGrey.main};
        }
      
        & svg[stroke] {
          stroke:  ${theme.palette.customGrey.main};
        }
      }

      & svg[fill] {
        fill:  ${theme.palette.customGrey.dark};
      }
    
      & svg[stroke] {
        stroke:  ${theme.palette.customGrey.dark};
      }
    `
);

const buttonCSS = {
  primary: PrimaryButton,
  secondary: SecondaryButton,
  text: TextButton,
  white: WhiteButton,
  grey: GreyButton,
};

const buttonSizes: ButtonSizes = {
  xs: {
    width: "max-content",
    height: "40px",
    padding: "10px 16px",
  },
  sm: {
    width: "100%",
    height: "44px",
    padding: "10px 16px",
  },
  md: {
    width: "100%",
    height: "56px",
    padding: "16px 20px",
  },
  lg: {
    width: "100%",
    height: "64px",
    padding: "20px 30px",
  },
};

const setActualButtonSizes = (
  size?: ButtonSize,
  variant?: ButtonVariant,
  maxWidth?: string,
  fullWidth?: boolean
) => {
  if (variant === "text") {
    return `
        width: max-content;
        height: auto;
        padding: 0;
    `;
  }

  return `
    width: ${fullWidth ? "100%" : buttonSizes[size ?? "lg"].width};
    height: ${buttonSizes[size ?? "lg"].height};
    padding: ${buttonSizes[size ?? "lg"].padding};
    max-width: ${maxWidth ? maxWidth : "500px"}; 
  `;
};

const StyledButton = styled(Button)<{
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $color?: string;
  $bgColor?: string;
  underlined?: boolean;
  maxWidth?: string;
  fullWidth?: boolean;
}>`
  ${({ $variant }) => buttonCSS[$variant ?? "primary"]}
  ${({ $size, $variant, maxWidth, fullWidth }) =>
    $size === "lg"
      ? setActualButtonSizes("md", $variant, maxWidth, fullWidth)
      : setActualButtonSizes($size, $variant, maxWidth, fullWidth)};
  color: ${({ $color }) => $color};
  background-color: ${({ $bgColor }) => $bgColor};
  text-decoration: ${({ underlined }) => underlined && "underline"};

  @media (min-width: 768px) {
    ${({ $size, $variant, maxWidth, fullWidth }) =>
      setActualButtonSizes($size, $variant, maxWidth, fullWidth)};
  }

  &:hover {
    text-decoration: ${({ underlined }) => underlined && "underline"};
  }

  & .MuiButton-endIcon,
  & .MuiButton-startIcon {
    & svg {
      width: 20px;
      height: 20px;
      transition: all 0.25s ease;
    }
  }
`;

const StyledLoadingButton = styled(LoadingButton)<{
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $color?: string;
  $bgColor?: string;
  underlined?: boolean;
  maxWidth?: string;
  fullWidth?: boolean;
}>`
  ${({ $variant }) => buttonCSS[$variant ?? "primary"]}
  ${({ $size, $variant, maxWidth, fullWidth }) =>
    $size === "lg"
      ? setActualButtonSizes("md", $variant, maxWidth, fullWidth)
      : setActualButtonSizes($size, $variant, maxWidth, fullWidth)};
  color: ${({ $color }) => $color};
  background-color: ${({ $bgColor }) => $bgColor};
  text-decoration: ${({ underlined }) => underlined && "underline"};
  min-width: 157px;

  @media (min-width: 768px) {
    ${({ $size, $variant, maxWidth, fullWidth }) =>
      setActualButtonSizes($size, $variant, maxWidth, fullWidth)};
  }
`;

export { StyledButton, StyledLoadingButton };
